import { compose } from 'ramda';
import Form from 'js/components/common/Form';
import React, { useState, useEffect } from 'react';
import { Button } from 'js/components/common/Button';
import { Loader } from 'js/components/common/Loader';
import { useDataFetch } from 'js/hooks/useDataFetch';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useFormatter } from 'js/hooks/useFormatter';
import { Input } from 'js/components/common/Fields/Input';
import { withComponent } from 'js/components/hocs/withComponent';
import { componentService } from 'js/services/component.service';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useComponentMethods } from 'js/hooks/component/useComponentMethods';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as hooks from './hooks';
import * as server from './server';
import * as helpers from './helpers';
import { PendingLimits } from './components/PendingLimits';
import { RESPONSIBLE_GAMING_SINGLE_BET_LIMIT } from './constants';

import style from './ResponsibleGamingSingleBetLimit.scss';

export const ResponsibleGamingSingleBetLimitBare = () => {
    const isMounted = useIsMounted();
    const formatter = useFormatter();
    const translate = useComponentTranslations();
    const validationConfig = helpers.getValidationConfig();
    const [initialValues, setInitialValues] = useState(null);
    const { showComponentError, showComponentMessage } = useComponentMethods();
    const [{ isLoading, error: singleBetLimitDataError, data: singleBetLimitData }, refetch] = useDataFetch({
        fetch: server.getPlayerSingleBetLimit,
        onResponse: helpers.parsePlayerSingleBetLimit
    });
    const onSubmit = hooks.useOnSubmit({ translate, refetch });

    useEffect(() => {
        if (!isMounted()) {
            return;
        }

        if (singleBetLimitDataError) {
            showComponentError(translate('generic.error'));

            return;
        }

        if (!singleBetLimitData) {
            return;
        }

        const { configuration } = singleBetLimitData;

        if (!configuration?.enabled) {
            showComponentMessage(translate('notEnabled'));

            return;
        }

        setInitialValues(helpers.getInitialValues(singleBetLimitData));
    }, [
        translate,
        isMounted,
        setInitialValues,
        showComponentError,
        singleBetLimitData,
        showComponentMessage,
        singleBetLimitDataError
    ]);

    if (!singleBetLimitData) {
        return <Loader />;
    }

    const { limit, pendingLimits } = singleBetLimitData;

    return (
        <div className={style.container}>
            <h2 className={style.title}>{translate('title')}</h2>
            <p className={style.description}>{translate('description')}</p>

            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                validationConfig={validationConfig}
                fieldsToReplaceComma={['limit']}
                render={({ submitting, pristine, submitError, handleSubmit }) => (
                    <form
                        className={style.form}
                        onSubmit={event => event.preventDefault()}
                    >
                        {submitError && <ErrorMessage error={submitError} animate={true} />}

                        <Input
                            type="text"
                            name="limit"
                            inputMode="decimal"
                            className={style.field}
                            label={translate('limit.label')}
                            placeholder={translate('limit.placeholder')}
                            currencySymbol={formatter.getCurrencySymbol(limit?.currencyCode)}
                            aria-label={translate([
                                'limit.label',
                                'limit.placeholder',
                                'playtech.mobile.system.input.aria-label'
                            ])}
                        />

                        {Boolean(pendingLimits.length) && (
                            <PendingLimits
                                fetchLimits={refetch}
                                translate={translate}
                                limits={pendingLimits}
                                isFormLoading={submitting || isLoading}
                            />
                        )}

                        <div className={style.controls}>
                            <Button
                                type="button"
                                onClick={handleSubmit}
                                className={style.submit}
                                disabled={submitting || pristine || isLoading}
                            >
                                {translate('submit')}
                            </Button>
                        </div>
                    </form>
                )}
            >
            </Form>
        </div>
    );
};

export const ResponsibleGamingSingleBetLimit = compose(
    withComponent({ isForLoggedIn: true })
)(ResponsibleGamingSingleBetLimitBare);

componentService.registerPageComponents({ [RESPONSIBLE_GAMING_SINGLE_BET_LIMIT]: ResponsibleGamingSingleBetLimit });
